/*
 * Un articol de autoservire, întreg.
 *
 * Are rută proprie — `/help/30` — și nu e o fereastră peste casetă. Un om care
 * urmează cinci pași pe ecran vrea să poată da înapoi, să trimită legătura unui
 * coleg, sau să o deschidă a doua oară mâine. Într-o fereastră, nimic din
 * astea trei nu merge.
 *
 * Ieșirea către tichet stă în josul articolului, mereu: dacă pașii n-au mers,
 * omul e chiar atunci în punctul în care are nevoie de ea.
 */

import { Link, useLocation, useParams } from 'react-router-dom';
import * as shop from '../api/shop';
import { ErrorState, Loading } from '../components/states';
import { t } from '../lib/i18n';
import { solveTimeLabel } from '../lib/selfServe';
import { useAsync } from '../lib/useAsync';

export function HelpArticleScreen() {
  const { id = '' } = useParams();
  const found = useAsync((signal) => shop.helpArticle(id, { signal }), [id]);
  /* Cautarea din care a venit omul, daca a venit dintr-una. Vezi ArticleCard. */
  const fromSearch = Number((useLocation().state as { searchId?: unknown } | null)?.searchId) || 0;

  if (found.status === 'loading') return <Loading label={t('Loading the article…')} />;

  if (found.status === 'error' && found.error) {
    return <ErrorState error={found.error} onRetry={found.reload} />;
  }

  const article = found.data?.article;

  if (!article) return null;

  const minutes = solveTimeLabel(article.self_serve_minutes);

  /*
   * Cererea pleaca DE LA articol, nu de la zero.
   *
   * Pana acum butonul ducea la `/tickets/new` gol, pe "Cum te putem ajuta?",
   * iar omul care tocmai citise pasii trebuia sa-si caute problema a doua
   * oara. Acum duce titlul (`q`, devine cautarea si subiectul) si numarul
   * articolului (`article`), in adresa - ca sa se poata reincarca sau trimite -
   * iar cautarea din care a venit, in stare: e a lui, nu a legaturii.
   */
  const requestUrl = `/tickets/new?${new URLSearchParams({
    article: String(article.id),
    q: article.title,
  }).toString()}`;

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{article.category?.name ?? t('Help')}</p>
          <h1 className="page-title">{article.title}</h1>
          {article.summary !== '' ? <p className="page-sub">{article.summary}</p> : null}
        </div>
      </header>

      {/* Numai când cineva a măsurat. Un implicit ar fi o cifră inventată, iar
          pe ecran nu se vede care cifră e măsurată și care inventată. */}
      {minutes !== '' ? <p className="article__time">{minutes}</p> : null}

      <div className="panel">
        {/*
          * Corpul e HTML scris de operatorii portalului în editorul lui —
          * aceiași oameni care scriu de ani de zile politici și soluții, în
          * același editor. Nu e conținut venit de la un utilizator al
          * magazinului, și nu e o poartă nouă.
          *
          * Curățat de etichete aici, pașii numerotați ar ajunge un bloc de
          * text, adică exact lucrul pe care articolul îl are de spus s-ar
          * pierde.
          */}
        <div className="article" dangerouslySetInnerHTML={{ __html: article.body }} />
      </div>

      <div className="article__after">
        <p>{t('Still stuck? Open a support request and it goes to the service desk.')}</p>
        <Link className="btn btn--primary" to={requestUrl} state={{ searchId: fromSearch }}>
          {t('Open a support request')}
        </Link>{' '}
        <Link className="btn" to="/catalog">{t('Back to catalog')}</Link>
      </div>
    </>
  );
}
